import { useEffect, useRef, memo } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { useAppStore } from '../../stores/appStore'
import { unifyRepeatsFromBase, applyPBR, loadTextureMaps, swapMeshUV } from './textureUtils'

function BedsideTableCmp({ tableUrl, texture, useUvChecker, rotateQuarterTurns, swapUV, bedLiftY }: { tableUrl: string; texture: any; useUvChecker: boolean; rotateQuarterTurns: number; swapUV: boolean; bedLiftY: number }) {
  const table = useLoader(FBXLoader, tableUrl)
  const tableBox = new THREE.Box3().setFromObject(table)
  const tableCenter = tableBox.getCenter(new THREE.Vector3())
  const tableMin = tableBox.min
  const BASE_SCALE = 0.05
  const tableYBase = -tableBox.min.y * BASE_SCALE
  // Select only bedsideTable slice to avoid re-renders from unrelated store updates
  const bedsideTable: any = useAppStore((s) => s.bedConfig.bedsideTable) as any
  const bedsideTableId = (bedsideTable as any)?.id as number | undefined
  // Get bed width to determine if we should render both sides or just one.
  // This hook must live before any early returns to keep hook ordering stable.
  const selectedSize = useAppStore((s) => s.bedConfig.bed.selectedSize) as number | undefined
  // Log bedside table payload only when the selected id changes to avoid render spam
  const lastLoggedTableId = useRef<number | null>(null)
  useEffect(() => {
    const bst: any = bedsideTable
    const id: number | null = bst?.id ?? null
    if (id && id !== lastLoggedTableId.current) {
      // eslint-disable-next-line no-console
      // debug: bedside table from store
      lastLoggedTableId.current = id
    }
  }, [bedsideTable?.id])

  const bedBox = (window as any).__bed_bbox__
  if (!bedBox || bedBox.isEmpty()) return null as any

  const isSingleBed = selectedSize !== undefined && selectedSize < 150
  // For single beds (<150cm), only render right side (i === 1), no mirroring
  const sidesToRender = isSingleBed ? [1] : [0, 1]

  const gapFromBed = 0.6
  // Use offsets from API (configured in admin)
  // Map x, y, z to sideGap, backOffset, heightOffset
  // Old hardcoded offsets (for reference):
  // ID 10, 11, 12: { x: -1.85, y: -0.27, z: 0.1 }
  const apiOffsets = (bedsideTable as any)?.offsets
  const customSideGap = apiOffsets?.x ?? 0
  const customBackOffset = apiOffsets?.z ?? 0
  const customHeightOffset = apiOffsets?.y ?? 0

  return (
    <>
      {sidesToRender.map((i) => {
        const tableSize = new THREE.Vector3().subVectors(tableBox.max, tableBox.min).multiplyScalar(BASE_SCALE)
        const halfW = tableSize.x * 0.5
        const isLeft = i === 0
        const centerZ = bedBox.min.z + (tableSize.z * 0.5) + customBackOffset
        const baseLeftX = bedBox.min.x - gapFromBed - halfW
        const baseRightX = bedBox.max.x + gapFromBed + halfW
        const centerX = isLeft ? (baseLeftX - customSideGap) : (baseRightX + customSideGap)
        const clone = table.clone()
        clone.scale.setScalar(BASE_SCALE)
        clone.position.set(-tableCenter.x * BASE_SCALE, -tableMin.y * BASE_SCALE, -tableCenter.z * BASE_SCALE)

        if (texture?.base_color_url || texture?.base_color_urls) {
          clone.traverse((child: any) => {
            if (child.isMesh && child.material) {
              // Clone geometry so each instance has its own UV data
              try {
                child.geometry = (child.geometry as THREE.BufferGeometry).clone()
                if (child.geometry && child.geometry.userData) {
                  delete child.geometry.userData._origUV
                }
              } catch {}
              swapMeshUV(child, swapUV, `bedside-table-${i}`)
              const mat = new THREE.MeshStandardMaterial()
              mat.color = new THREE.Color(0xffffff)
              ;(mat as any).emissive?.setRGB?.(0, 0, 0)
              ;(mat as any).vertexColors = false
              const turns: number = Math.abs(rotateQuarterTurns || 0) % 4
              
              const loader = new THREE.TextureLoader()
              const maps = loadTextureMaps(texture, loader)
              
              // Clone textures so left/right tables don't share texture instances
              const clonedMaps = {
                base: maps.base ? maps.base.clone() : null,
                normal: maps.normal ? maps.normal.clone() : null,
                roughness: maps.roughness ? maps.roughness.clone() : null,
                metalness: maps.metalness ? maps.metalness.clone() : null,
                ao: maps.ao ? maps.ao.clone() : null,
                displacement: maps.displacement ? maps.displacement.clone() : null,
              }
              
              if (clonedMaps.base) {
                clonedMaps.base.flipY = false
                // @ts-ignore
                if ((THREE as any).SRGBColorSpace) {
                  // @ts-ignore
                  clonedMaps.base.colorSpace = (THREE as any).SRGBColorSpace
                }
                clonedMaps.base.wrapS = clonedMaps.base.wrapT = THREE.RepeatWrapping
                if ((clonedMaps.base as any).center && typeof (clonedMaps.base as any).center.set === 'function') {
                  ;(clonedMaps.base as any).center.set(0.5, 0.5)
                }
                clonedMaps.base.rotation = turns ? (Math.PI / 2) * turns : 0
                // Only mirror texture for left side on beds >= 150cm
                if (isLeft && !isSingleBed) {
                  clonedMaps.base.wrapS = THREE.MirroredRepeatWrapping
                  clonedMaps.base.repeat.x = 1
                  clonedMaps.base.offset.x = 0
                }
              }
              
              applyPBR({
                mat,
                origMat: child.material,
                type: 'wood',
                meshName: child.name,
                maps: clonedMaps,
                rotateTurns: turns,
                hasUV2: !!child.geometry.attributes.uv2,
              })
              mat.needsUpdate = true
              child.material = mat
              unifyRepeatsFromBase(child.material)
            }
          })
        }

        clone.traverse((child: any) => {
          if (child.isMesh) {
            child.castShadow = true
            child.receiveShadow = true
          }
        })

        // Coerce on_floor flag from various possible shapes (boolean, string, number, alt keys)
        const coerceBool = (v: any): boolean => v === true || v === 'true' || v === 1 || v === '1'
        const bedsideTableAny: any = bedsideTable
        const onFloor = coerceBool(
          bedsideTableAny?.on_floor ??
          bedsideTableAny?.onFloor ??
          bedsideTableAny?.options?.on_floor ??
          bedsideTableAny?.options?.onFloor
        )
        // Use scene-provided bed lift for deterministic repositioning
        // Align clone so its local bottom is at Y=0; group Y determines world elevation
        // If on floor, keep at Y=0; otherwise lift by bed height
        const tableY = (onFloor ? 0 : bedLiftY) + customHeightOffset

        return (
          <group key={i} position={[centerX, tableY, centerZ]}>
            {/* Only mirror left side for beds >= 150cm */}
            <group scale={[isLeft && !isSingleBed ? -1 : 1, 1, 1]}>
              <primitive object={clone} />
            </group>
          </group>
        )
      })}
    </>
  )
}

const BedsideTable = memo(BedsideTableCmp)
export default BedsideTable



